import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { businessPhaseLabel } from './CaptureLifecyclePanel';
import { useUpdateCaptureProject } from './hooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
export function HypothesisCard({ captureProjectId, summary, readOnly = false }) {
    const update = useUpdateCaptureProject();
    const [editing, setEditing] = useState(false);
    const [hypothesis, setHypothesis] = useState(summary.opportunityHypothesis);
    const [stage, setStage] = useState(summary.businessPhase || 'unknown');
    // Resync from the server whenever we're not actively editing (e.g. after a
    // save refetch, or if the summary changes underneath us).
    useEffect(()=>{
        if (!editing) {
            setHypothesis(summary.opportunityHypothesis);
            setStage(summary.businessPhase || 'unknown');
        }
    }, [
        summary.opportunityHypothesis,
        summary.businessPhase,
        editing
    ]);
    const save = ()=>update.mutate({
            captureProjectId,
            name: summary.name,
            businessPhase: stage,
            opportunityHypothesis: hypothesis,
            accountId: summary.accountId,
            customerOrgId: summary.customerOrgId
        }, {
            onSuccess: ()=>setEditing(false)
        });
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            children: [
                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                    className: "flex flex-row items-center justify-between space-y-0 py-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Opportunity hypothesis"
                        }),
                        !editing && /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "ghost",
                            size: "sm",
                            "data-testid": Testids.capture.hypothesisEditButton,
                            onClick: ()=>setEditing(true),
                            iconLeft: /*#__PURE__*/ _jsx(Pencil, {
                                className: "h-3.5 w-3.5"
                            }),
                            children: "Edit"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraCardContent, {
                    className: "space-y-3",
                    children: editing ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                value: hypothesis,
                                onChange: (e)=>setHypothesis(e.target.value),
                                rows: 4,
                                placeholder: "Why we think we can win — incumbent weaknesses, our discriminators, customer hot buttons.",
                                "data-testid": Testids.capture.hypothesisInput
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-end gap-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Business phase"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: stage,
                                                onValueChange: setStage,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "w-[180px]",
                                                        "data-testid": Testids.capture.stageSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: [
                                                            'shaping',
                                                            'qualifying',
                                                            'pursuing',
                                                            'unknown'
                                                        ].map((value)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: value,
                                                                children: businessPhaseLabel(value)
                                                            }, value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "ml-auto flex gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "secondary",
                                                size: "sm",
                                                onClick: ()=>setEditing(false),
                                                children: "Cancel"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                onClick: save,
                                                disabled: update.isPending,
                                                "data-testid": Testids.capture.hypothesisSaveButton,
                                                children: update.isPending ? 'Saving…' : 'Save'
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: summary.opportunityHypothesis || 'No hypothesis captured yet.'
                    })
                })
            ]
        })
    });
}
